(() => {
  'use strict';

  const STORAGE_KEYS = {
    neck: 'ganton:fit-size:neck',
    sleeve: 'ganton:fit-size:sleeve'
  };

  const COLLECTION_FILTERS = {
    neck: 'filter.v.option.neck size',
    sleeve: 'filter.v.option.sleeve length'
  };

  const IGNORED_VALUES = new Set([
    'RES',
    'S-RES',
    'SX-RES',
    'L-RES',
    'LX-RES'
  ]);

  const isValidSizeValue = (value) => {
    if (!value) return false;

    const cleanValue = String(value).trim();

    if (!cleanValue) return false;

    if (IGNORED_VALUES.has(cleanValue.toUpperCase())) {
      return false;
    }

    if (cleanValue.startsWith('gid://shopify/FilterSettingGroup/')) {
      return false;
    }

    return true;
  };

  const readStorage = (key) => {
    try {
      const value = sessionStorage.getItem(key);

      return isValidSizeValue(value) ? value : '';
    } catch (error) {
      return '';
    }
  };

  const writeStorage = (key, value) => {
    if (!isValidSizeValue(value)) return false;

    try {
      sessionStorage.setItem(key, String(value).trim());
      return true;
    } catch (error) {
      return false;
    }
  };

  const removeStorage = (key) => {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      // Storage failure must not affect normal storefront behaviour.
    }
  };

  const getStoredSize = () => {
    return {
      neck: readStorage(STORAGE_KEYS.neck),
      sleeve: readStorage(STORAGE_KEYS.sleeve)
    };
  };

  const dispatchSizeChange = (source) => {
    document.dispatchEvent(
      new CustomEvent('ganton:fit-size-change', {
        detail: {
          source,
          ...getStoredSize()
        }
      })
    );
  };

  const capturePdpInput = (input) => {
    if (!(input instanceof HTMLInputElement)) {
      return false;
    }

    if (!input.classList.contains('js-option')) {
      return false;
    }

    const name = String(input.name || '').toLowerCase();
    const value = String(input.value || '').trim();

    if (!isValidSizeValue(value)) {
      return false;
    }

    /*
     * Use the real radio names from the existing PDP:
     *
     * template--...-neck-size-selector
     * template--...-sleeve-length-selector
     */
    if (name.includes('neck-size-selector')) {
      const saved = writeStorage(STORAGE_KEYS.neck, value);

      if (saved) {
        dispatchSizeChange('pdp');
      }

      return saved;
    }

    if (name.includes('sleeve-length-selector')) {
      const saved = writeStorage(STORAGE_KEYS.sleeve, value);

      if (saved) {
        dispatchSizeChange('pdp');
      }

      return saved;
    }

    return false;
  };

  const captureCollectionInput = (input) => {
    if (!(input instanceof HTMLInputElement)) {
      return false;
    }

    const value = String(input.value || '').trim();

    if (!isValidSizeValue(value)) {
      return false;
    }

    if (
      input.name === COLLECTION_FILTERS.neck &&
      input.checked
    ) {
      const saved = writeStorage(STORAGE_KEYS.neck, value);

      if (saved) {
        dispatchSizeChange('collection');
      }

      return saved;
    }

    if (
      input.name === COLLECTION_FILTERS.sleeve &&
      input.checked
    ) {
      const saved = writeStorage(STORAGE_KEYS.sleeve, value);

      if (saved) {
        dispatchSizeChange('collection');
      }

      return saved;
    }

    return false;
  };

  const captureInteraction = (event) => {
    const input = event.target;

    if (!(input instanceof HTMLInputElement)) {
      return;
    }

    /*
     * PDP variant selectors.
     */
    if (input.classList.contains('js-option')) {
      capturePdpInput(input);
      return;
    }

    /*
     * Collection Search & Discovery filters.
     */
    if (
      input.name === COLLECTION_FILTERS.neck ||
      input.name === COLLECTION_FILTERS.sleeve
    ) {
      captureCollectionInput(input);
    }
  };

  /*
   * Listen for both.
   *
   * Click captures the direct user interaction immediately.
   * Change covers keyboard interaction and normal radio/checkbox changes.
   *
   * Writing the same value twice is harmless because sessionStorage
   * simply replaces the existing value.
   */
  document.addEventListener('click', captureInteraction, true);
  document.addEventListener('change', captureInteraction, true);

  /*
   * Capture an already-applied collection filter when loading a
   * collection page directly.
   *
   * Deliberately do NOT capture :checked PDP options here.
   */
 const initialiseState = () => {
  let collectionSizeFound = false;

  /*
   * Priority 1:
   * An active collection size filter represents explicit
   * customer intent and should be preserved on the PDP.
   */
  document
    .querySelectorAll(
      `input[name="${COLLECTION_FILTERS.neck}"]:checked,
       input[name="${COLLECTION_FILTERS.sleeve}"]:checked`
    )
    .forEach((input) => {
      if (captureCollectionInput(input)) {
        collectionSizeFound = true;
      }
    });

  if (collectionSizeFound) {
    return;
  }

  /*
   * Priority 2:
   * On a PDP, inspect the actual selected customer-facing
   * Neck Size and Sleeve Length.
   *
   * If both are valid, that complete selected combination
   * becomes the current sizing context.
   */
  const checkedPdpInputs = Array.from(
    document.querySelectorAll(
      'input.js-option:checked'
    )
  );

  let selectedNeck = null;
  let selectedSleeve = null;

  checkedPdpInputs.forEach((input) => {
    const inputName = String(
      input.name || ''
    ).toLowerCase();

    const value = String(
      input.value || ''
    ).trim();

    if (!isValidSizeValue(value)) {
      return;
    }

    if (
      inputName.includes(
        'neck-size-selector'
      )
    ) {
      selectedNeck = input;
    }

    if (
      inputName.includes(
        'sleeve-length-selector'
      )
    ) {
      selectedSleeve = input;
    }
  });

  /*
   * Only replace remembered sizing when the PDP has
   * a complete valid size combination.
   *
   * RES / S-RES / SX-RES / L-RES / LX-RES are rejected
   * by isValidSizeValue().
   */
  if (selectedNeck && selectedSleeve) {
    writeStorage(
      STORAGE_KEYS.neck,
      selectedNeck.value
    );

    writeStorage(
      STORAGE_KEYS.sleeve,
      selectedSleeve.value
    );

    dispatchSizeChange(
      'pdp-initial'
    );

    return;
  }

  /*
   * No complete customer-facing PDP size is selected.
   * In that situation, leave any previously remembered
   * customer sizing untouched.
   */
};


  window.GantonFitSize = {
    get: getStoredSize,

    clear() {
      removeStorage(STORAGE_KEYS.neck);
      removeStorage(STORAGE_KEYS.sleeve);
    },

    isValidValue: isValidSizeValue,

    ignoredValues: Array.from(IGNORED_VALUES),

    /*
     * Temporary diagnostic method, also useful during development.
     */
    inspect() {
      return {
        stored: getStoredSize(),
        pdpInputs: Array.from(
          document.querySelectorAll('input.js-option')
        ).map((input) => ({
          name: input.name,
          value: input.value,
          checked: input.checked,
          unavailable: input.classList.contains('is-unavailable')
        }))
      };
    }
  };

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    initialiseState,
    { once: true }
  );
} else {
  initialiseState();
}
})();